import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Loader2, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { ForcePasswordChangePage } from './ForcePasswordChangePage';

export const UniversalLoginPage = () => {
    const navigate = useNavigate();
    const { login, token } = useAuth();
    
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [mustChangePassword, setMustChangePassword] = useState(false);
    const [loginData, setLoginData] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.email || !formData.password) {
            toast.error('Bitte füllen Sie alle Felder aus');
            return;
        }

        setLoading(true);
        
        try {
            // Login über AuthContext - ohne organization_code
            const { user, organization } = await login(formData.email, formData.password, null);
            
            // Check if password change is required
            if (user.must_change_password) {
                setLoginData({ user, organization });
                setMustChangePassword(true);
                setLoading(false);
                return;
            }
            
            toast.success(`Willkommen, ${user.name}!`);
            redirectUser(user, organization);
            
        } catch (error) {
            console.error('Login failed:', error);
            if (error.response?.status === 401) {
                toast.error('Ungültige E-Mail oder Passwort');
            } else {
                toast.error('Anmeldung fehlgeschlagen');
            }
        } finally {
            setLoading(false);
        }
    };

    const redirectUser = (user, organization) => {
        // Basierend auf Rolle weiterleiten
        if (user.role === 'superadmin') {
            navigate('/superadmin/dashboard');
        } else if (user.role === 'org_admin') {
            navigate(`/o/${organization.organization_code}/admin/referrals`);
        } else if (user.role === 'sales_rep') {
            navigate(`/o/${organization.organization_code}/sales`);
        } else {
            navigate(`/o/${organization.organization_code}/dashboard`);
        }
    };

    const handlePasswordChanged = () => {
        // After password change, redirect to the appropriate dashboard
        if (loginData) {
            toast.success(`Willkommen, ${loginData.user.name}!`);
            // Update user in state to remove must_change_password
            const updatedUser = { ...loginData.user, must_change_password: false };
            redirectUser(updatedUser, loginData.organization);
        }
    };

    // Show force password change page if required
    if (mustChangePassword && loginData) {
        return (
            <ForcePasswordChangePage
                token={token}
                user={loginData.user}
                organization={loginData.organization}
                onPasswordChanged={handlePasswordChanged}
            />
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
            <div className="w-full max-w-md">
                {/* Logo/Branding */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">
                        Incentive-Plattform
                    </h1>
                    <p className="text-slate-400">
                        Melden Sie sich an, um fortzufahren
                    </p>
                </div>

                <Card className="border-0 shadow-2xl">
                    <CardHeader className="space-y-1 pb-4">
                        <CardTitle className="text-2xl">Anmelden</CardTitle>
                        <CardDescription>
                            Geben Sie Ihre Zugangsdaten ein
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">E-Mail</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="ihre@email.de"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    disabled={loading}
                                    data-testid="email-input"
                                    autoComplete="email"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="password">Passwort</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    disabled={loading}
                                    data-testid="password-input"
                                    autoComplete="current-password"
                                />
                            </div>

                            <Button
                                type="submit"
                                className="w-full bg-blue-600 hover:bg-blue-700"
                                disabled={loading}
                                data-testid="login-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Anmelden...
                                    </>
                                ) : (
                                    <>
                                        <LogIn className="mr-2 h-4 w-4" />
                                        Anmelden
                                    </>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                <p className="text-center text-sm text-slate-500 mt-6">
                    Sie haben noch keinen Zugang? Wenden Sie sich an Ihren Administrator.
                </p>
            </div>
        </div>
    );
};
